import BaseModel from './BaseModel';
import moment from 'moment';
import {  Utils } from '../services';
import { parseProgrammationTimeToken } from '../components/HoursField/normalizeHourInput';
import {
    format_single_day_programmation_label,
    format_unique_weekday_dates_label,
    is_single_calendar_day_period,
} from '../services/programmationPeriodDays';

/** Class representing a programmation. */
class Programmation extends BaseModel {
    /**
     * @param {function} t
     * @param {string} lang
     * @param {{ start_at?: *, period_type?: string, period_nb?: number|string }|null} [period]
     * @returns {string|null}
     */
    describeDays(t, lang, period = null) {
        if (period && is_single_calendar_day_period(period.period_type, period.period_nb)) {
            const single_day = format_single_day_programmation_label(t, lang, period.start_at);
            if (single_day)
                return single_day;
        }

        if (!this.days?.length)
            return null;

        const unique_dates = format_unique_weekday_dates_label(t, lang, this.days, period);
        if (unique_dates)
            return unique_dates;

        if (this.days.length === 7)
            return t('programmations.all_days');

        const dayNames = this.days.map(d => {
            let dayName = moment().locale(lang).day(d).format('dddd');

            switch (lang) {
                case 'fr':
                    dayName += 's'; // e.g. "jeudis"
                    break;
                // No plural mark in German and English
                case 'de':
                case 'en':
                default:
                    // fallback: nothing to do
                    break;
            }
            return dayName;
        });

        return t('programmations.each_days', {
            days: Utils.joinWithComma(dayNames, t)
        });
    }

    describeTimes(t, lang) {
        if (!this.times?.length)
            return null;

        return t('programmations.at', {
            time: Utils.joinWithComma(
                this.times.map((time) => {
                    const parsed = parseProgrammationTimeToken(time);
                    const start = parsed?.start || String(time).split('-')[0];
                    const startLabel = moment()
                        .locale(lang)
                        .hour(start.split(':')[0])
                        .minutes(start.split(':')[1])
                        .format('LT');
                    if (!parsed?.end)
                        return startLabel;
                    const endLabel = moment()
                        .locale(lang)
                        .hour(parsed.end.split(':')[0])
                        .minutes(parsed.end.split(':')[1])
                        .format('LT');
                    return `${startLabel}–${endLabel}`;
                }),
                t
            )
        });
    }

    describeSupport(t) {
        if (!this.options?.support)
            return null;

        return t(`programmations.support_${this.options.support}`);
    }

    describeTasks(t, lang) {
        if (!this.tasks?.length)
            return null;

        const activities = {};
        this.tasks.forEach(task => {
            if (!task.activity)
                return;

            const name = Utils.localized_or_fallback(task.activity.name, lang);
            activities[name] = (activities[name] || 0) + 1;
        });

        return Utils.joinWithComma(Object.keys(activities).map(name => `${activities[name]} x ${name}`), t);
    }

    prepareForUpdate() {
        const prepared = super.prepareForUpdate();
        if (Array.isArray(prepared.events)) {
            prepared.events = prepared.events.filter((ev) => ev && ev.id);
        }
        if (Array.isArray(prepared.tasks)) {
            prepared.tasks = prepared.tasks.filter((task) => task?.activity?.id || task?.activity_id);
        }
        return prepared;
    }
}

export default Programmation;
